/* eslint-disable no-nested-ternary */
import React, { Component } from 'react';
import Modal from 'react-modal';
import { Link } from 'react-router-dom';
import Tooltip from '../../common/Tooltip';
import ActionButton from '../../common/ActionButton';
import RefreshButton from '../../common/refreshbutton';
import SearchBar from '../common/SearchBar';
import CollapsibleTable from '../bookings/CollapsibleTable';
import { ApiDropDown, PublishedDropDown } from '../common/dropdowns';
import SendSMSContainer from '../freelancers/send_sms';
import SendNotificationContainer from '../freelancers/SendNotification';
import { withPushFiltersToUrl } from '../common/decorators';
import { BulkActions } from '../freelancers/BulkActions';
import history from '../../../history';
import { TFreelancerData, TMeta, TPageNotification, TUser } from '../../../types';
import BooleanDropdown from '../common/BooleanDropdown';
import RemoveFromListContainer from './RemoveFromList';
import { RenameAvailabilityList } from './RenameListAction';
import { ConfirmDeleteAvailabilityList } from './DeleteListAction';
import { executeHttpRequest } from '../../../epics/api';
import { AddToAvailabilityListModal } from '../../freelancer-details/AddToAvailabilityList';
import FreelancerDetailsAgentNotes from '../../freelancer-details/freelancerDetailsAgentNotes';
import { ROOT_URL_V2 } from '../../../config';
import { AvailabilityListActions } from './AvailabilityListActions';


type TAvailabilityTableProps = {
    data: TFreelancerData[];
    meta: TMeta;
    pages: number;
    showPaginationBottom: boolean;
    loading: boolean;
    notification: TPageNotification;
    user: TUser;
    selectedFreelancers: TFreelancerData[];
    reloadData: () => void;
    setSelectedFreelancers: (allSelectedFreelancers: TFreelancerData[]) => void;
    currentPage?: number;
    onPageChange?: () => void;
    onSortedChange?: () => void;
    onFilterChange?: (filterKey?: string, filterValue?: { value: string }) => void;
    currentUrlParams: URLSearchParams;
};

type TAvailabilityTableState = {
    isBulkMode: boolean;
    isBulkExclusiveMode: boolean;
    showSMS: boolean;
    showNotification: boolean;
    showAddToList: boolean;
    showRemoveFromList: boolean;
    isListSelected: boolean;
    showRenameList: boolean;
    showDeleteList: boolean;
    availabilityLists: { label: string, value: string | number }[];
    lastCheckedRowIndex: number;
    lastCheckedRowArrayIndex: number;
};

type TTableRow = {
    original: TFreelancerData;
    value: string;
    isExpanded: boolean;
};

// @ts-ignore
@withPushFiltersToUrl
export default class AvailabilityTable extends Component<
    TAvailabilityTableProps,
    TAvailabilityTableState
> {
    state: TAvailabilityTableState = {
        isBulkMode: false,
        isBulkExclusiveMode: false,
        showSMS: false,
        showNotification: false,
        showAddToList: false,
        showRemoveFromList: false,
        isListSelected: false,
        showRenameList: false,
        showDeleteList: false,
        availabilityLists: [],
        lastCheckedRowIndex: null,
        lastCheckedRowArrayIndex: null,
    };

    componentDidMount() {
        this.fetchAvailabilityLists();
    }

    componentDidUpdate(prevProps: Readonly<TAvailabilityTableProps>) {
        if (prevProps.currentUrlParams !== this.props.currentUrlParams) {
            this.showListAction();
        }
    }

    showListAction = () => {
        if (this.props.currentUrlParams.has('availability_lists')) {
            this.setState({ isListSelected: true });
        } else {
            this.setState({ isListSelected: false });
        }
    }

    fetchAvailabilityLists() {
        executeHttpRequest('availability-lists/')
        .then((response => {
            const content = response.data?.content;
            const options = content && content.map((option: any) => ({
                label: option.name,
                value: option.id,
            }));
            this.setState({ availabilityLists: options });
        }))
        .catch(err => {
            // eslint-disable-next-line no-console
            console.log(err);
            this.props.notification.error(
                'Error', 'Could not load availability lists. Please refresh the page.'
            );
        });
    }

    canExpandRow = () => true;

    onRowCheckboxChange = (rowInfo: TTableRow, checked: boolean, shiftKeyPressed: boolean) => {
        const { lastCheckedRowIndex, lastCheckedRowArrayIndex, isBulkExclusiveMode } = this.state;

        const rowIndex = rowInfo.original.id;
        const rowArrayIndex = this.props.data.findIndex(
            (object: { id: number; }) => object.id === rowIndex
        );
        let allSelectedFreelancers;

        if (shiftKeyPressed && this.props.selectedFreelancers.length > 0) {
            // handle selection with 'shift' key
            const start = Math.min(lastCheckedRowArrayIndex, rowArrayIndex);
            const end = Math.max(lastCheckedRowArrayIndex, rowArrayIndex);

            const rowSelectedFreelancers = this.props.data.filter(
                (_: never, index: number) => index >= start && index <= end
            );

            if (this.props.selectedFreelancers.some(
                freelancer => freelancer.id === lastCheckedRowIndex)
            ) {
                allSelectedFreelancers = new Set([
                    ...this.props.selectedFreelancers,
                    ...rowSelectedFreelancers
                ]);
            } else {
                allSelectedFreelancers = this.props.selectedFreelancers.filter(
                    freelancer => !rowSelectedFreelancers.some(
                        selected => selected.id === freelancer.id
                    )
                );
            }

            allSelectedFreelancers = Array.from(allSelectedFreelancers);

        } else if ((!isBulkExclusiveMode && checked) || (isBulkExclusiveMode && !checked)) {
                allSelectedFreelancers = this.props.selectedFreelancers.concat(
                    rowInfo.original,
                );
            } else {
                allSelectedFreelancers = this.props.selectedFreelancers.filter(
                    item => item.id !== rowInfo.original.id,
                );
            }

        this.setState({
            lastCheckedRowIndex: rowIndex,
            lastCheckedRowArrayIndex: rowArrayIndex,
        });
        this.props.setSelectedFreelancers(allSelectedFreelancers);
    };

    onToggleBulkMode = () => {
        this.setState(prevState => ({
            isBulkMode: !prevState.isBulkMode,
            isBulkExclusiveMode: false,
        }));
        this.props.setSelectedFreelancers([]);
    };

    onBulkAction = (key: string, val: { value: string }) => {
        if (val.value === 'send_sms') {
            this.setState({
                showSMS: true,
            });
        }
        if (val.value === 'send_notification') {
            this.setState({
                showNotification: true,
            });
        }
        if (val.value === 'add_workers_to_list') {
            this.setState({
                showAddToList: true,
            });
        }
        if (val.value === 'remove_workers_from_list') {
            this.setState({
                showRemoveFromList: true,
            });
        }
    };

    onListAction = (key: string, val: { value: string }) => {
        if (val.value === 'rename_availability_list') {
            this.setState({
                showRenameList: true,
            });
        }
        if (val.value === 'delete_availability_list') {
            this.setState({
                showDeleteList: true,
            });
        }
    };

    selectAllRows = () => {
        let allSelectedFreelancers;
        const allFreelancers = this.props.data;
        allSelectedFreelancers = new Set([
            ...this.props.selectedFreelancers,
            ...allFreelancers
        ]);
        allSelectedFreelancers = Array.from(allSelectedFreelancers);
        this.props.setSelectedFreelancers(allSelectedFreelancers);
    }

    deselectAllRows = () => {
        this.props.setSelectedFreelancers([]);
    }

    isRowChecked = (rowInfo: TTableRow) => {
        const { isBulkExclusiveMode } = this.state;
        const isInSelectedFreelancers = this.props.selectedFreelancers.some(
            item => item.id === rowInfo.original.id,
        );
        return isBulkExclusiveMode
            ? !isInSelectedFreelancers
            : isInSelectedFreelancers;
    };

    onActionSuccess = () => {
        this.setState({
            isBulkMode: false,
            isBulkExclusiveMode: false,
        });
        this.props.setSelectedFreelancers([]);
    };

    onAddToListSuccess = (id: string) => {
        this.onActionSuccess();
        this.props.onFilterChange('availability_lists', { value: id });
        if (!this.state.availabilityLists.find(list => list.value === id)) {
            this.fetchAvailabilityLists();
        }
    }

    onRenameListSuccess = () => {
        this.fetchAvailabilityLists();
    }

    onDeleteListSuccess = () => {
        // reset filters to default after removing availability list
        this.props.onFilterChange('availability_lists', { value: '' });
        this.fetchAvailabilityLists();
    }

    updateFreelancerData = (id: number) => (fields: any) => (
        executeHttpRequest(`freelancers/${id}/`, 'PATCH', fields)
    ).then(response => {
        this.props.reloadData();
        return response;
    }).catch(error => {
        this.props.notification.error(
            'Error',
            'Failed to update worker\'s notes',
        );
        return error;
    })

    getCsvDownloadUrl = (currentUrlParams: URLSearchParams) =>
        `${ROOT_URL_V2}freelancers/export/?${currentUrlParams.toString()}&format=csv`;

    render() {
        const currentUrlParams = new URLSearchParams(history.location.search);

        return (
            <div className="table-container">
                <div className="table-filter-container">
                    <PublishedDropDown
                        onFilterChange={this.props.onFilterChange}
                    />
                    <BooleanDropdown
                        onFilterChange={this.props.onFilterChange}
                        currentUrlParams={currentUrlParams}
                        filterKey="is_archived"
                        falseLabel="Live"
                        trueLabel="Archived"
                    />
                    {this.state.availabilityLists && (
                        <ApiDropDown
                            onFilterChange={this.props.onFilterChange}
                            currentUrlParams={currentUrlParams}
                            url="availability-lists/"
                            filterKey="availability_lists"
                            labelKey="name"
                            valueKey="id"
                            verboseNamePlural="availability lists"
                            preloadedOptions={this.state.availabilityLists}
                        />
                    )}
                    <SearchBar
                        filterKey="search"
                        onFilterChange={this.props.onFilterChange}
                        currentUrlParams={currentUrlParams}
                    />
                    <div className="table-action-bar">
                        <BulkActions
                            user={this.props.user}
                            items={this.props.selectedFreelancers}
                            onBulkAction={this.onBulkAction}
                            totalNumberOfItemsInTable={
                                this.props.meta && this.props.meta.count
                            }
                            isBulkExclusiveMode={this.state.isBulkExclusiveMode}
                            isBulkMode={this.state.isBulkMode}
                            selectedJobs={[]}
                            isAvailabilityRoute={true}
                            selectAllRows={this.selectAllRows}
                            deselectAllRows={this.deselectAllRows}
                        />
                        {this.state.availabilityLists && (
                            <AvailabilityListActions
                                user={this.props.user}
                                onListAction={this.onListAction}
                                isListSelected={this.state.isListSelected}
                            />
                        )}
                        <RefreshButton reloadData={this.props.reloadData} />
                        <ActionButton
                            iconClass="icon-navigation-next-4"
                            tooltip="Export as CSV"
                            onAction={() => {
                                window.location.href = this.getCsvDownloadUrl(currentUrlParams);
                            }}
                        />
                    </div>
                </div>
                <Modal
                    className="none"
                    contentLabel="SMS Modal"
                    isOpen={this.state.showSMS}
                >
                    <SendSMSContainer
                        freelancers={this.props.selectedFreelancers}
                        exclusive={this.state.isBulkExclusiveMode}
                        totalNumberOfItemsInTable={
                            this.props.meta && this.props.meta.count
                        }
                        notification={this.props.notification}
                        onActionSuccess={this.onActionSuccess}
                        closeModal={() => this.setState({ showSMS: false })}
                    />
                </Modal>
                <Modal
                    className="none"
                    contentLabel="Notification Modal"
                    isOpen={this.state.showNotification}
                >
                    <SendNotificationContainer
                        freelancers={this.props.selectedFreelancers}
                        notification={this.props.notification}
                        onActionSuccess={this.onActionSuccess}
                        closeModal={() => this.setState({ showNotification: false })}
                    />
                </Modal>
                {this.state.showAddToList && (
                    <AddToAvailabilityListModal
                        freelancers={this.props.selectedFreelancers}
                        isOpen={this.state.showAddToList}
                        handleClose={() => this.setState({ showAddToList: false })}
                        notification={this.props.notification}
                        onActionSuccess={this.onAddToListSuccess}
                        preloadedOptions={this.state.availabilityLists}
                    />
                )}
                <Modal
                    className="availability-action-modal"
                    contentLabel="Remove from List Modal"
                    isOpen={this.state.showRemoveFromList}
                    onRequestClose={() => this.setState({ showRemoveFromList: false })}
                    ariaHideApp={false}
                >
                    <RemoveFromListContainer
                        freelancers={this.props.selectedFreelancers}
                        availabilityLists={this.state.availabilityLists}
                        currentUrlParams={currentUrlParams}
                        notification={this.props.notification}
                        onActionSuccess={this.onActionSuccess}
                        closeModal={() => this.setState({ showRemoveFromList: false })}
                        reloadData={this.props.reloadData}
                    />
                </Modal>
                <Modal
                    className="availability-confirm-modal"
                    contentLabel="Rename Avaliability List"
                    isOpen={this.state.showRenameList}
                    onRequestClose={() => this.setState({ showRenameList: false })}
                    >
                    <RenameAvailabilityList
                        currentUrlParams={currentUrlParams}
                        notification={this.props.notification}
                        closeModal={() => this.setState({ showRenameList: false })}
                        reloadData={this.props.reloadData}
                        onActionSuccess={this.onRenameListSuccess}
                        availabilityLists={this.state.availabilityLists}
                    />
                </Modal>
                <Modal
                    className="availability-confirm-modal"
                    contentLabel="Rename Avaliability List"
                    isOpen={this.state.showDeleteList}
                    onRequestClose={() => this.setState({ showDeleteList: false })}
                    >
                    <ConfirmDeleteAvailabilityList
                        currentUrlParams={currentUrlParams}
                        notification={this.props.notification}
                        closeModal={() => this.setState({ showDeleteList: false })}
                        reloadData={this.props.reloadData}
                        onActionSuccess={this.onDeleteListSuccess}
                        availabilityLists={this.state.availabilityLists}
                    />
                </Modal>
                <CollapsibleTable
                    page={this.props.currentPage}
                    pageSize={0}
                    data={this.props.data}
                    pages={this.props.pages}
                    columns={this.tableColumns}
                    className="table-content"
                    loading={this.props.loading}
                    showPaginationBottom={this.props.showPaginationBottom}
                    showPageSizeOptions={false}
                    manual={true}
                    // eslint-disable-next-line no-return-assign, no-sequences
                    ref={table => (
                        // @ts-ignore
                        // eslint-disable-next-line no-sequences
                        (this.table = table), this.props.setTable(table)
                    )}
                    onPageChange={this.props.onPageChange}
                    onSortedChange={this.props.onSortedChange}
                    onRowCheckboxChange={this.onRowCheckboxChange}
                    onToggleBulkMode={this.onToggleBulkMode}
                    isBulkMode={this.state.isBulkMode}
                    isRowChecked={this.isRowChecked}
                    canExpandRow={this.canExpandRow}
                    expandUsingURLPrimaryKey={false}
                    SubComponent={(row: TTableRow) => (
                        <div className="expanded-content expanded-content-with-agent-notes">
                            <ActionButton
                                text="View details"
                                onAction={() =>
                                    history.push(
                                        `/operations/freelancer/${row.original.id}/`,
                                    )
                                }
                            />
                            {(
                                this.props.user.agency.features.includes('dashboard_sms') &&
                                this.props.user.permissions.includes('auth.sms_dashboard_allowed')
                            ) && (
                                <>
                                    <ActionButton
                                        text="Send notification..."
                                        onAction={() => {
                                            this.setState({
                                                isBulkMode: false,
                                                isBulkExclusiveMode: false,
                                                showNotification: true,
                                            });

                                            this.props.setSelectedFreelancers([
                                                // @ts-ignore
                                                row.original,
                                            ]);
                                        }}
                                        requiredFeature="dashboard_sms"
                                        requiredPermission="auth.sms_dashboard_allowed"
                                    />
                                    <ActionButton
                                        text="Send SMS..."
                                        onAction={() => {
                                            this.setState({
                                                isBulkMode: false,
                                                isBulkExclusiveMode: false,
                                                showSMS: true,
                                            });

                                            this.props.setSelectedFreelancers([
                                                // @ts-ignore
                                                row.original,
                                            ]);
                                        }}
                                        requiredFeature="dashboard_sms"
                                        requiredPermission="auth.sms_dashboard_allowed"
                                    />
                                </>
                            )}
                            <ActionButton
                                text='Remove from list...'
                                onAction={() => {
                                    this.setState({
                                        isBulkMode: false,
                                        isBulkExclusiveMode: false,
                                        showRemoveFromList: true,
                                    });

                                    this.props.setSelectedFreelancers([
                                        // @ts-ignore
                                        row.original,
                                    ]);
                                }}
                                requiredRole="agent"
                            />
                        </div>
                    )}
                />
            </div>
        );
    }

    tableColumns = [
        {
            Header: 'Ref',
            accessor: 'reference_number',
            headerClassName: 'small-col',
            className: 'small-col',
            Cell: (row: TTableRow) => (
                <div>
                    <Link
                        className="group-link"
                        to={`/operations/freelancer/${row.original.id}`}
                    >
                        {row.value}
                    </Link>
                </div>
            ),
        },
        {
            Header: 'Photo',
            accessor: 'freelancer_detail.photo_thumbnail_medium',
            sortable: false,
            headerClassName: 'xsmall-col',
            className: 'xsmall-col',
            Cell: (row: TTableRow) => {
                const freelancerImg = row.original.photo_thumbnail_large
                    ? row.original.photo_thumbnail_large
                    : '/static/images/default_profile.png';
                return (
                    <div>
                        <a
                            className="group-link"
                            href={freelancerImg}
                            target="_blank"
                            rel="noreferrer"
                        >
                            <div
                                className="avatar"
                                style={{
                                    backgroundImage: `url(${freelancerImg})`,
                                }}
                            />
                        </a>
                    </div>
                );
            },
        },

        {
            Header: 'Name',
            accessor: 'full_name',
            Cell: (row: TTableRow) => (
                <span title={row.value}>{row.value} </span>
            ),
        },
        {
            Header: 'Service',
            accessor: 'service_key',
            headerClassName: 'small-col',
            className: 'small-col upper-case',
        },
        {
            Header: 'Role(s)',
            accessor: 'service_role',
            headerClassName: 'small-col',
            className: 'small-col upper-case',
            Cell: (row: TTableRow) => (
                <span title={row.value}>{row.value} </span>
            ),
        },
        {
            Header: 'Status',
            sortable: false,
            headerClassName: 'xsmall-col',
            className: 'xsmall-col upper-case',
            Cell: (row: TTableRow) =>
                !row.original.is_active ? (
                    <Tooltip tooltip="This worker is disabled">
                        <i className="icon icon-medium with-margin icon-help-circle grey" />
                    </Tooltip>
                ) : row.original.has_upcoming_booking ? (
                    <Tooltip tooltip="This worker is published and in an upcoming booking">
                        <i className="icon icon-medium with-margin icon-check-circle-22 blue" />
                    </Tooltip>
                ) : row.original.published ? (
                    <Tooltip tooltip="This worker is published and receives offers">
                        <i className="icon icon-medium with-margin icon-check-circle-22 green" />
                    </Tooltip>
                ) : (
                    <Tooltip
                        tooltip={`This worker is unpublished because of: ${row.original.unpublication_reason}`}
                    >
                        <i className="icon icon-medium with-margin icon-remove-circle-11 red" />
                    </Tooltip>
                ),
        },
        {
            Header: 'Mobile',
            accessor: 'mobile',
            headerClassName: 'small-col',
            className: 'small-col',
            sortable: false,
            show: this.props.user.isAgent,
            Cell: (row: TTableRow) => {
                const mobileLink = `tel:${row.value}`;
                return (
                    <a
                        className="group-link"
                        href={mobileLink}
                        title={row.value}
                    >
                        {row.value && row.value.length === 11
                            ? `${row.value.substr(0, 5)} ${row.value.substr(
                                  5,
                                  11,
                              )}`
                            : row.value}
                    </a>
                );
            },
        },
        {
            Header: 'Email',
            accessor: 'email',
            sortable: false,
            show: this.props.user.isAgent,
            Cell: (row: TTableRow) => {
                const mailToAddress = `mailto:${row.value}`;
                return (
                    <a
                        className="group-link"
                        href={mailToAddress}
                        title={row.value}
                    >
                        {row.value}
                    </a>
                );
            },
        },
        {
            // used with 'expanded-content-with-agent-notes' css class for action buttons styling
            Header: 'Notes',
            accessor: 'agent_notes',
            sortable: false,
            show: this.props.user.isAgent,
            Cell: (row: TTableRow) =>
                row.isExpanded
                    ? <FreelancerDetailsAgentNotes
                            agentNotes={row.value}
                            onSave={this.updateFreelancerData(row.original.id)}
                            isTable={true}
                    />
                    : row.value
        },
    ];
}
